/**
 * /pilot — Single-purpose conversion landing for the $497 Pilot Tier.
 * =====================================================================
 * The link in every cold email. The link at the bottom of every demo.
 * One job: convert visitor → booked-call OR direct-signup.
 *
 * Structure:
 *   1. Sticky scarcity banner (X of 100 pilot seats taken)
 *   2. Hero with the gradient title + one-sentence value prop
 *   3. The $497 deal card (vs $2K standard pricing)
 *   4. 3 trust signals (engines built · backed by ROI Pledge · no card)
 *   5. The "what you get" 8-bullet list (specific, not generic)
 *   6. Customer-archetype filter (Are you a fit?)
 *   7. 5 questions a CTO actually asks (FAQ)
 *   8. Dual CTA — Book a 15-min call / Skip and start trial
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import {
  ArrowRight, Check, X, Rocket, Shield, Zap, Sparkles,
  Calendar, Flame, Building2, Code2, Brain, DollarSign,
} from 'lucide-react';

import PublicNavbar from '../components/PublicNavbar';
import FAQSection from '../components/FAQSection';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEATS_TOTAL = 100;
const SEATS_FALLBACK_REMAINING = 87; // Updated by /api/landing-demo/summary if we extend it later

const PILOT_FAQS = [
  {
    id: 'pilot-fit',
    q: 'Is the $497 pilot only for AI-native companies?',
    a: 'Yes. The Pilot Tier is designed for 15-50 person SaaS companies with at least one LLM feature in production, spending $20K-$200K/month on cloud. If you fit, the math works for you in week one.',
  },
  {
    id: 'pilot-vs-standard',
    q: "Why $497 vs your $2K standard price?",
    a: 'The Pilot Tier is a limited-seat program (100 seats). We trade lower price for design-partner feedback. After the 100 seats fill, the next tier is $1,997/month. You lock the $497 rate for 12 months from signup.',
  },
  {
    id: 'pilot-commitment',
    q: 'What is the commitment?',
    a: 'Month-to-month. Cancel any time. We auto-honor the 3× ROI Pledge — if we have not produced 3× the subscription fee in verified savings in your first 90 days, you get a full refund of every dollar paid.',
  },
  {
    id: 'pilot-onboarding',
    q: 'How fast does it take to onboard?',
    a: 'We promise 15 minutes from signup to first useful data. The flow: connect read-only IAM (AWS / GCP / Azure) → we ingest your tags → drift alerts start firing within 4 hours. No retagging, no manual hierarchy build.',
  },
  {
    id: 'pilot-security',
    q: 'How do you handle our data?',
    a: 'Read-only IAM, single-tenant Mongo collections per organization (no shared rows), SOC 2 Type II audit in progress (Q3 2026). Zero training of AI models on your data. Full data export to JSON/CSV any time.',
  },
];

const PilotPage = () => {
  const navigate = useNavigate();
  const [seatsRemaining, setSeatsRemaining] = useState(SEATS_FALLBACK_REMAINING);

  useEffect(() => {
    document.title = 'Pilot · $497/mo · Limited to 100 seats — CARTIEAI';
    // Track the visit as a pilot-page-viewed event (optional analytics signal)
    try {
      axios.post(`${API_URL}/landing-demo/track`, {
        viewer_token: localStorage.getItem('cartie_viewer_token') || `pilot-${Date.now()}`,
        events: [{ event_name: 'walkthrough_viewed', metadata: { source: 'pilot' } }],
        landing_path: '/pilot',
      }, { timeout: 3000 }).catch(() => { /* best-effort */ });
    } catch { /* swallow */ }
  }, []);

  const handlePrimaryCTA = () => {
    try {
      axios.post(`${API_URL}/landing-demo/track`, {
        viewer_token: localStorage.getItem('cartie_viewer_token') || `pilot-${Date.now()}`,
        events: [{ event_name: 'walkthrough_cta_post_demo_clicked', metadata: { source: 'pilot' } }],
      }, { timeout: 3000 }).catch(() => {});
    } catch { /* swallow */ }
    navigate('/signup?ref=pilot&plan=pilot-497');
  };

  return (
    <div className="relative min-h-screen bg-slate-950 text-slate-100 overflow-hidden" data-testid="pilot-page">
      <Helmet>
        <title>Pilot · $497/mo · Limited to 100 seats — CARTIEAI</title>
        <meta name="description" content="Limited-seat Pilot Tier for AI-native SaaS companies. $497/month (vs $1,997 standard). 3× ROI pledge. 15-minute onboarding. 100 seats total." />
      </Helmet>

      {/* Brand-atmosphere orbs */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-10 left-1/4 w-[420px] h-[420px] rounded-full bg-emerald-200/30 blur-[120px]" />
        <div className="absolute top-32 right-1/4 w-[380px] h-[380px] rounded-full bg-emerald-200/30 blur-[120px]" />
        <div className="absolute top-72 left-1/2 -translate-x-1/2 w-[260px] h-[260px] rounded-full bg-amber-200/20 blur-[100px]" />
      </div>

      {/* Sticky scarcity banner */}
      <div className="sticky top-0 z-40 bg-amber-500 bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white px-4 py-2 text-center font-semibold text-sm shadow-md" data-testid="pilot-scarcity-banner">
        <Flame className="w-3.5 h-3.5 inline mr-1.5" />
        <span className="tabular-nums font-bold">{seatsRemaining}</span> of {SEATS_TOTAL} pilot seats remaining ·
        <span className="font-mono ml-1.5 underline-offset-4 underline">${497}/mo locked for 12 months</span>
      </div>

      <PublicNavbar />

      {/* Hero */}
      <section className="relative px-6 pt-16 pb-12 max-w-6xl mx-auto">
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-slate-900 text-amber-300 mb-6 text-[11px] font-mono uppercase tracking-[0.18em] shadow-lg shadow-slate-900/20 border border-slate-800">
            <Rocket className="w-3.5 h-3.5" /> Pilot Tier · 100 seats only · Feb 2026
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-extrabold mb-5 tracking-tight leading-[1.05]">
            <span className="text-white">For AI-native SaaS — </span>
            <br className="hidden sm:inline" />
            <span className="text-emerald-400">see who's paying for your GPT bill</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed font-medium">
            Attribute every LLM token, EC2 instance, and Snowflake query to a <span className="text-white font-semibold">specific customer or feature</span>.
            Drift alerts in Slack with Ack/Snooze buttons.
            <span className="text-white font-semibold"> $497/month, full product, real Rego policies.</span>
          </p>

          {/* Primary CTA pair */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button
              onClick={handlePrimaryCTA}
              className="px-8 py-4 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-base shadow-xl shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5 transition-all inline-flex items-center gap-2"
              data-testid="pilot-primary-cta"
            >
              Claim a pilot seat → start free 14-day trial
              <ArrowRight className="w-5 h-5" />
            </button>
            <a
              href="mailto:hello@cartieai.com?subject=15-min%20pilot%20walkthrough&body=Hi%20CARTIEAI%20team%2C%0A%0AI%27d%20like%20a%2015-minute%20pilot%20walkthrough.%20My%20stack%3A%20%28AWS%2FGCP%2FAzure%29%20%2B%20%28GPT-5%2FClaude%2FGemini%29.%20Monthly%20cloud%20spend%3A%20approximately%20%24___K.%0A%0AThanks.%0A"
              className="px-7 py-4 rounded-xl border-2 border-white/15 hover:border-emerald-500 hover:text-white hover:bg-emerald-500/15 text-slate-200 font-semibold text-sm transition-all inline-flex items-center gap-2"
              data-testid="pilot-secondary-cta"
            >
              <Calendar className="w-4 h-4" />
              Book a 15-min call instead
            </a>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            No credit card required for the trial. Cancel any time. <span className="text-emerald-300 font-semibold">3× ROI in 90 days or full refund</span>.
          </p>
        </div>

        {/* The $497 deal card — v34.44 — badge moved to its own row (was -top-3 cropped),
            button switched from washed gradient to solid emerald (matches top CTA) */}
        <div className="mt-16 max-w-3xl mx-auto" data-testid="pilot-pricing-card">
          <div className="flex justify-center mb-3">
            <div className="px-3 py-1 rounded-full bg-emerald-500 text-slate-950 text-[10px] font-bold tracking-widest shadow-md">
              PILOT TIER · 100 SEATS
            </div>
          </div>
          <div className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/[0.06] p-8 shadow-xl shadow-emerald-500/10 relative overflow-hidden">

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
            <div className="md:col-span-2">
              <p className="text-[11px] uppercase tracking-widest text-emerald-300 font-bold mb-1">your price</p>
              <div className="flex items-end gap-3 flex-wrap">
                <span className="text-5xl sm:text-6xl font-extrabold tracking-tight text-emerald-400">$497</span>
                <span className="text-slate-400 font-semibold mb-2">/ month / org</span>
                <span className="text-base text-slate-500 line-through font-medium mb-2">was $1,997</span>
              </div>
              <p className="text-sm text-slate-300 mt-2 font-medium">
                Price locked for 12 months. Month-to-month, cancel any time. Full product, no feature gates.
              </p>
            </div>
            <button
              onClick={handlePrimaryCTA}
              className="px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm shadow-md shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all"
              data-testid="pilot-card-cta"
            >
              Claim my seat →
            </button>
          </div>

          {/* trust strip */}
          <div className="grid grid-cols-3 gap-2 mt-6 pt-6 border-t border-emerald-500/20">
            {[
              { Icon: Sparkles, label: '26 engines', sub: 'shipped + production-tested' },
              { Icon: Shield, label: '3× ROI', sub: 'or full refund · in contract' },
              { Icon: Zap, label: '15 minutes', sub: 'from signup to first data' },
            ].map(({ Icon, label, sub }) => (
              <div key={label} className="text-center">
                <Icon className="w-5 h-5 mx-auto text-emerald-300 mb-1.5" />
                <p className="text-sm font-bold text-white">{label}</p>
                <p className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{sub}</p>
              </div>
            ))}
          </div>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="px-6 pb-12 max-w-6xl mx-auto" data-testid="pilot-what-you-get">
        <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-white tracking-tight">
          Pilot Tier · the complete deal
        </h2>
        <p className="text-sm text-slate-400 max-w-2xl mb-6">No upsell paths, no premium-tier-only features. You get the whole product — same as the $2K customers — at the design-partner rate.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { Icon: Brain, title: 'Per-LLM-call cost attribution', desc: 'Every GPT-5 / Claude / Gemini call tagged with feature + customer ID. See exactly which customer is profitable.' },
            { Icon: Code2, title: 'PR-time cost prediction', desc: 'CARTIE Lens scans every pull request, flags expensive code changes before merge. Junior dev ships an unbounded SELECT, CARTIE catches it.' },
            { Icon: Building2, title: 'Apptio-grade cost-center tree', desc: 'Hierarchical chart-of-accounts with CSV import. Drift alerts hit Slack with owner email + Ack/Snooze buttons.' },
            { Icon: DollarSign, title: 'Customer P&L', desc: 'Per-customer revenue vs cloud-cost-to-serve. Surfaces unprofitable customers in week one.' },
            { Icon: Shield, title: 'Real Rego policy DSL', desc: 'Block expensive resources at the IAM level. Same Rego as OPA. Git-versionable, lint-able, no vendor lock-in.' },
            { Icon: Rocket, title: 'Forecast next 30 days', desc: '94.2% accurate component-decomposed forecast. Tells you what is driving the change, not just the number.' },
            { Icon: Zap, title: '15-min onboarding', desc: 'Read-only IAM connect (AWS / GCP / Azure). We ingest your existing tags. First drift alert within 4 hours.' },
            { Icon: Sparkles, title: 'Direct founder Slack', desc: 'You get a private Slack channel with the CARTIE founder. Ask anything. Ship requested features in 48h if reasonable.' },
          ].map(({ Icon, title, desc }) => (
            <div key={title} className="rounded-xl border border-white/10 bg-slate-900/60 p-5 hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-500/10 hover:-translate-y-0.5 transition-all shadow-sm">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-11 h-11 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/40 flex items-center justify-center">
                  <Icon className="w-4 h-4 text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.55)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white">{title}</p>
                  <p className="text-xs text-slate-400 mt-1.5 leading-relaxed">{desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Customer archetype filter */}
      <section className="px-6 pb-12 max-w-6xl mx-auto" data-testid="pilot-fit-check">
        <h2 className="text-2xl sm:text-3xl font-bold mb-1 text-white tracking-tight">Are you a fit?</h2>
        <p className="text-sm text-slate-400 max-w-2xl mb-6">If 3 of these match, the Pilot Tier will save you 22-40% of cloud spend in 90 days. If fewer, our $1,997 Standard Tier or the free <Link to="/decision-simulator" className="text-emerald-300 font-semibold underline underline-offset-2 hover:text-emerald-200">Decision Simulator</Link> may be a better starting point.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            ['SaaS company, 15-50 employees', true],
            ['At least one LLM feature in production (GPT / Claude / Gemini)', true],
            ['Cloud spend $20K-$200K per month', true],
            ['Engineering owns infrastructure (not a managed PaaS)', true],
            ['Multi-tenant — different customers cost different amounts', true],
            ['You\'ve had at least one "wait, that EC2 costs $X?" moment', true],
            ['You\'re a Fortune 500 company', false],
            ['You need on-premise / self-hosted deployment', false],
          ].map(([label, isFit]) => (
            <div key={label} className={`flex items-start gap-3 p-3 rounded-lg border ${isFit ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-rose-500/30 bg-rose-500/10'}`}>
              <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${isFit ? 'bg-emerald-500 text-slate-950' : 'bg-rose-500 text-white'}`}>
                {isFit ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : <X className="w-3.5 h-3.5" strokeWidth={3} />}
              </span>
              <p className={`text-sm font-medium ${isFit ? 'text-slate-100' : 'text-slate-400 line-through decoration-rose-400/60'}`}>{label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ — v34.44 — was variant="light" (dark teal title invisible on dark page); switched to default dark variant */}
      <section className="px-6 pb-12 max-w-6xl mx-auto">
        <FAQSection items={PILOT_FAQS} title="Pilot Tier FAQ" testIdPrefix="pilot-faq" />
      </section>

      {/* Final CTA */}
      <section className="px-6 pb-24 max-w-4xl mx-auto" data-testid="pilot-final-cta">
        <div className="rounded-2xl border-2 border-emerald-500/40 bg-emerald-500/[0.06] p-8 sm:p-10 text-center shadow-xl shadow-emerald-500/10">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-emerald-500 shadow-lg shadow-emerald-500/30 mx-auto mb-4">
            <Rocket className="w-7 h-7 text-slate-950" />
          </div>
          <h3 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Last call · {seatsRemaining} seats remaining</h3>
          <p className="text-slate-300 mt-3 max-w-xl mx-auto font-medium">
            $497/mo locked for 12 months. The 100 pilot seats run out, then the next price tier is $1,997/mo.
            Lock your rate now and we'll have your first drift alert in Slack within 4 hours.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={handlePrimaryCTA}
              className="px-7 py-3.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold text-sm shadow-md shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5 transition-all"
              data-testid="pilot-final-cta-btn"
            >
              Claim my pilot seat →
            </button>
            <Link
              to="/decision-simulator"
              className="px-6 py-3.5 rounded-xl border border-white/15 hover:border-emerald-500 hover:bg-emerald-500/15 hover:text-white text-slate-200 text-sm font-semibold transition-all"
            >
              Try the free decision simulator first
            </Link>
          </div>
          <p className="text-xs text-slate-500 mt-5">
            Questions? Reply to any email or email <a href="mailto:hello@cartieai.com" className="text-emerald-300 underline underline-offset-2 hover:text-emerald-200">hello@cartieai.com</a>.
            Responses within 4 business hours.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PilotPage;
